// Table — tabulka s sipkovou navigaciou (ustava 54.2)
// API:
//   <Table onRowSelect={(rowKey) => ...} selectedRow={rowKey}>
//     <THead><TR><TH>Meno</TH><TH align="right">Suma</TH></TR></THead>
//     <TBody>
//       <TR rowKey="1"><TD>...</TD><TD align="right">...</TD></TR>
//     </TBody>
//   </Table>
//
// Klavesnicova navigacia (sek. 54.2):
//   ↑ / ↓        — predosly / dalsi riadok
//   Home / End   — prvy / posledny
//   Enter        — onRowOpen(rowKey)
//   Space        — onRowSelect (toggle)
//   PageUp/Down  — o 10 riadkov

import React, { useRef, useEffect, useCallback } from 'react'
import { COLORS, RADIUS, TYPO, SPACE } from '../theme.js'

export function Table({
  children,
  selectedRow,
  onRowSelect,
  onRowOpen,
  striped = false,
  compact = false,
  fullWidth = true,
  style = {},
  tableStyle = {},   // REK_REGISTER_V1C: štýl samotnej <table> (napr. tableLayout: 'fixed' — stĺpce sa delia o šírku, nič nepretečie)
}) {
  const tableRef = useRef(null)

  // Zoznam rowKey-ov v poradi (zbierame z DOM v efekte)
  const rowKeysRef = useRef([])

  useEffect(() => {
    if (!tableRef.current) return
    const rows = tableRef.current.querySelectorAll('[data-row-key]')
    rowKeysRef.current = Array.from(rows).map(r => r.getAttribute('data-row-key'))
  })

  const moveSelection = useCallback((delta) => {
    const keys = rowKeysRef.current
    if (!keys.length || !onRowSelect) return
    const idx = keys.indexOf(String(selectedRow))
    let next
    if (idx === -1) next = 0
    else next = Math.max(0, Math.min(keys.length - 1, idx + delta))
    onRowSelect(keys[next])
    // Focus riadku ak je viditelny
    const el = tableRef.current?.querySelector(`[data-row-key="${keys[next]}"]`)
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }, [selectedRow, onRowSelect])

  const handleKey = useCallback((e) => {
    if (!onRowSelect) return
    switch (e.key) {
      case 'ArrowDown':  e.preventDefault(); moveSelection(1); break
      case 'ArrowUp':    e.preventDefault(); moveSelection(-1); break
      case 'PageDown':   e.preventDefault(); moveSelection(10); break
      case 'PageUp':     e.preventDefault(); moveSelection(-10); break
      case 'Home':       e.preventDefault(); moveSelection(-9999); break
      case 'End':        e.preventDefault(); moveSelection(9999); break
      case 'Enter':      if (selectedRow != null && onRowOpen) { e.preventDefault(); onRowOpen(selectedRow) } break
      default: break
    }
  }, [moveSelection, onRowSelect, onRowOpen, selectedRow])

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', overflow: 'auto', ...style }}>
      <table
        ref={tableRef}
        tabIndex={0}
        onKeyDown={handleKey}
        style={{
          width: fullWidth ? '100%' : 'auto',
          borderCollapse: 'collapse',
          fontSize: TYPO.sizes.base,
          fontFamily: 'inherit',
          color: COLORS.text,
          background: COLORS.bg,
          outline: 'none',
          ...tableStyle
        }}
        data-striped={striped ? '1' : '0'}
        data-compact={compact ? '1' : '0'}
        data-selected={selectedRow ?? ''}
      >
        {children}
      </table>
    </div>
  )
}

export function THead({ children, style = {} }) {
  return (
    <thead style={{
      background: COLORS.bgSoft,
      borderBottom: `2px solid ${COLORS.line}`,
      ...style
    }}>
      {children}
    </thead>
  )
}

export function TBody({ children }) {
  return <tbody>{children}</tbody>
}

export function TR({ children, rowKey, onClick, style = {}, ...rest }) {
  // Pre TR sa nemozne dat ref/key cez deti — vlozime data-row-key atribut
  const [hover, setHover] = React.useState(false)
  return (
    <tr
      data-row-key={rowKey}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        background: hover ? COLORS.hover : 'inherit',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 80ms ease',
        ...style
      }}
      {...rest}
    >
      {children}
    </tr>
  )
}

export function TH({ children, align = 'left', width, style = {}, ...rest }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '8px 10px',
        fontSize: TYPO.sizes.sm,
        fontWeight: TYPO.weights.semibold,
        color: COLORS.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        width,
        ...style
      }}
      {...rest}
    >
      {children}
    </th>
  )
}

export function TD({ children, align = 'left', mono = false, style = {}, ...rest }) {
  return (
    <td
      style={{
        textAlign: align,
        padding: '8px 10px',
        borderTop: `1px solid ${COLORS.lineSoft}`,
        fontFamily: mono ? TYPO.mono : 'inherit',
        verticalAlign: 'middle',
        ...style
      }}
      {...rest}
    >
      {children}
    </td>
  )
}

export default Table
